import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import {
  createWishlist,
  deleteWishlist,
  getWishlist,
  listWishlists,
  removeWishlistItem,
  removeWishlistSavedOffer,
  saveWishlistOffer,
  updateWishlist,
} from './api'
import type { WishlistSavePayload } from './types'

const getWishlistQueryScope = (memberId?: string) => memberId || 'anonymous'

export const wishlistQueryKeys = {
  all: (memberId?: string) => ['wishlists', getWishlistQueryScope(memberId)] as const,
  detail: (memberId: string | undefined, wishlistId: string) =>
    [...wishlistQueryKeys.all(memberId), wishlistId] as const,
}

interface UseWishlistIndexOptions {
  enabled?: boolean
}

export function useWishlistIndex(options?: UseWishlistIndexOptions) {
  const memberId = useAppSelector((state) => state.auth.account.memberId)

  return useQuery({
    queryKey: wishlistQueryKeys.all(memberId),
    queryFn: listWishlists,
    enabled: config.ENABLE_TRIP_PLANNER && Boolean(memberId) && (options?.enabled ?? true),
  })
}

export function useWishlistDetail(wishlistId: string) {
  const memberId = useAppSelector((state) => state.auth.account.memberId)

  return useQuery({
    queryKey: wishlistQueryKeys.detail(memberId, wishlistId),
    queryFn: () => getWishlist(wishlistId),
    enabled: config.ENABLE_TRIP_PLANNER && Boolean(memberId && wishlistId),
  })
}

export function useCreateWishlist() {
  const memberId = useAppSelector((state) => state.auth.account.memberId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (name: string) => createWishlist(name),
    onSuccess: async(wishlist) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: wishlistQueryKeys.all(memberId) }),
        queryClient.invalidateQueries({
          queryKey: wishlistQueryKeys.detail(memberId, wishlist.id),
        }),
      ])
    },
  })
}

export function useUpdateWishlist() {
  const memberId = useAppSelector((state) => state.auth.account.memberId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ wishlistId, name }: { wishlistId: string; name: string }) =>
      updateWishlist(wishlistId, name),
    onSuccess: async(wishlist) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: wishlistQueryKeys.all(memberId) }),
        queryClient.invalidateQueries({
          queryKey: wishlistQueryKeys.detail(memberId, wishlist.id),
        }),
      ])
    },
  })
}

export function useSaveWishlistOffer() {
  const memberId = useAppSelector((state) => state.auth.account.memberId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: WishlistSavePayload) => saveWishlistOffer(payload),
    onSuccess: async() => {
      await queryClient.invalidateQueries({ queryKey: wishlistQueryKeys.all(memberId) })
    },
  })
}

export function useRemoveWishlistItem() {
  const memberId = useAppSelector((state) => state.auth.account.memberId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ wishlistId, savedOfferId }: { wishlistId: string; savedOfferId: string }) =>
      removeWishlistItem(wishlistId, savedOfferId),
    onSuccess: async(_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: wishlistQueryKeys.all(memberId) }),
        queryClient.invalidateQueries({
          queryKey: wishlistQueryKeys.detail(memberId, variables.wishlistId),
        }),
      ])
    },
  })
}

export function useRemoveWishlistSavedOffer() {
  const memberId = useAppSelector((state) => state.auth.account.memberId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (savedOfferId: string) => removeWishlistSavedOffer(savedOfferId),
    onSuccess: async() => {
      await queryClient.invalidateQueries({ queryKey: wishlistQueryKeys.all(memberId) })
    },
  })
}

export function useDeleteWishlist() {
  const memberId = useAppSelector((state) => state.auth.account.memberId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (wishlistId: string) => deleteWishlist(wishlistId),
    onSuccess: async(_data, wishlistId) => {
      await queryClient.invalidateQueries({ queryKey: wishlistQueryKeys.all(memberId) })
      queryClient.removeQueries({ queryKey: wishlistQueryKeys.detail(memberId, wishlistId) })
    },
  })
}
